import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarBlank, Clock, CheckCircle, CaretRight, CaretLeft, ArrowRight,
} from "@phosphor-icons/react";
import { getSchedulesList } from "../lib/api";

const CARD_BG = "https://images.unsplash.com/photo-1635776062127-d379bfcba9f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGxpZ2h0JTIwZ3JhZGllbnQlMjBwdXJwbGUlMjBjeWFufGVufDB8fHx8MTc4Mzc2NDkxNHww&ixlib=rb-4.1.0&q=85";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return isNaN(d) ? iso : `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const isChoice = (e) => e.is_choice_filling || /choice.?fill|locking/i.test(e.activity || "");
const roundLabel = (r) => (r == null ? "Counselling" : r >= 100 ? "Stray Vacancy Round" : `Round ${r}`);

function bannerInfo(sched, refIso) {
  const evs = (sched.events || []).filter((e) => e.start).slice().sort((a, b) => a.start.localeCompare(b.start));
  if (!evs.length) return { state: "empty" };
  const firstStart = evs[0].start;
  const lastEnd = evs.reduce((m, e) => ((e.end || e.start) > m ? e.end || e.start : m), evs[0].start);
  if (refIso > lastEnd) return { state: "completed", firstStart, lastEnd, current: evs[evs.length - 1] };
  if (refIso < firstStart) return { state: "upcoming", firstStart, lastEnd, current: evs[0] };
  const active = evs.filter((e) => e.start <= refIso && (e.end || e.start) >= refIso);
  const current = active.find(isChoice) || active[active.length - 1] || evs.find((e) => e.start > refIso) || evs[evs.length - 1];
  return { state: active.length ? "ongoing" : "gap", firstStart, lastEnd, current };
}

export default function ScheduleBanner({ body }) {
  const nav = useNavigate();
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    setSlides([]); setIdx(0);
    if (!body) return;
    getSchedulesList(body)
      .then((r) => { if (alive) setSlides((r.data?.schedules || []).filter((s) => (s.events || []).length)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [body]);

  // auto-rotate
  useEffect(() => {
    if (slides.length <= 1) return;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(timer.current);
  }, [slides.length]);

  const sched = slides[idx];
  const wholeRange = useMemo(() => {
    if (!sched) return null;
    const evs = (sched.events || []).filter((e) => e.start).map((e) => e.start).sort();
    const ends = (sched.events || []).filter((e) => e.start).map((e) => e.end || e.start).sort();
    return evs.length ? { start: evs[0], end: ends[ends.length - 1] } : null;
  }, [sched]);

  if (!body || !slides.length || !sched) return null;

  const todayIso = new Date().toISOString().slice(0, 10);
  const refIso = todayIso;
  const info = bannerInfo(sched, refIso);
  const cur = info.current || {};
  const streamY = `${(sched.stream || "").toUpperCase()} ${sched.year}`;
  const open = () => nav(`/counselling/schedule/${sched.body_slug}/${sched.slug}`);

  const STATE = {
    ongoing: { label: "Happening now", color: "text-cyan-300", dot: "bg-[#00E5FF]" },
    gap: { label: "Coming up next", color: "text-amber-300", dot: "bg-amber-400" },
    upcoming: { label: "Starts soon", color: "text-amber-300", dot: "bg-amber-400" },
    completed: { label: "Completed", color: "text-emerald-300", dot: "bg-emerald-400" },
    empty: { label: "", color: "", dot: "" },
  }[info.state] || { label: "", color: "text-white", dot: "bg-white" };

  return (
    <div className="w-full bg-black" data-testid="schedule-banner">
      <div
        role="button"
        onClick={open}
        data-testid="schedule-banner-slide"
        className="relative overflow-hidden cursor-pointer group max-w-[1400px] mx-auto"
      >
        <img src={CARD_BG} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-950/95 via-[#2A1B6D]/85 to-[#6043EF]/40" />

        <div className="relative z-10 px-4 sm:px-6 py-5 sm:py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-8 min-h-[132px]">
          {/* left — round + range */}
          <div className="min-w-0 md:max-w-[46%]">
            <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.25em] text-cyan-300">{sched.short} · {streamY}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <h2 className="text-2xl sm:text-3xl font-black tracking-tighter text-white drop-shadow font-heading">
                {info.state === "completed" ? roundLabel(cur.round) : roundLabel(cur.round)}
              </h2>
              {info.state === "completed" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/90 text-white text-[9px] font-black uppercase tracking-wider" data-testid="banner-completed">
                  <CheckCircle size={11} weight="fill" /> Completed
                </span>
              )}
            </div>
            <p className="text-white/60 text-[10px] font-bold uppercase tracking-[0.2em] mt-0.5">Timetable</p>
            <span className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full bg-cyan-400/15 border border-cyan-300/40 text-cyan-100 text-xs sm:text-sm font-bold font-mono tabular-nums">
              <CalendarBlank size={14} weight="fill" className="text-cyan-300" />
              {wholeRange ? `${fmt(wholeRange.start)} – ${fmt(wholeRange.end)}` : ""}
            </span>
          </div>

          {/* middle — current timeline item */}
          <div className="flex-1 min-w-0 rounded-2xl bg-black/35 backdrop-blur-xl border border-white/10 px-4 py-3">
            <div className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider ${STATE.color}`}>
              <span className={`relative flex h-2 w-2`}>
                {info.state === "ongoing" && <span className={`absolute inline-flex h-full w-full rounded-full ${STATE.dot} opacity-60 animate-ping`} />}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${STATE.dot}`} />
              </span>
              {STATE.label}
            </div>
            <p className={`text-base sm:text-lg font-bold leading-tight mt-1 ${isChoice(cur) ? "text-cyan-200" : "text-white"}`}>{cur.activity || "—"}</p>
            <p className="text-white/70 text-xs font-mono tabular-nums mt-0.5">
              {fmt(cur.start)}{cur.end && cur.end !== cur.start ? ` → ${fmt(cur.end)}` : ""}{cur.time ? ` · ${cur.time}` : ""}
            </p>
          </div>

          {/* right — CTA */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white text-[#09090B] text-xs font-black uppercase tracking-wider group-hover:gap-2.5 transition-all">
              View schedule <ArrowRight size={15} weight="bold" />
            </span>
          </div>
        </div>

        {/* carousel controls */}
        {slides.length > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + slides.length) % slides.length); }}
              data-testid="banner-prev" className="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8 grid place-items-center rounded-full bg-black/40 backdrop-blur border border-white/15 text-white hover:bg-black/60">
              <CaretLeft size={14} weight="bold" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % slides.length); }}
              data-testid="banner-next" className="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8 grid place-items-center rounded-full bg-black/40 backdrop-blur border border-white/15 text-white hover:bg-black/60">
              <CaretRight size={14} weight="bold" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
              {slides.map((s, i) => (
                <button key={s.slug + i} onClick={(e) => { e.stopPropagation(); setIdx(i); }} data-testid={`banner-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/40"}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
